import React from 'react';
import { ErrorBoundary } from '@hotjar/error-handler';
import { logError } from '@hotjar/insights-common/lib/logger';
import { upsellLogger } from '@hotjar/webapp/domains/revenue/utils';
import {
  DatadogActionType,
  DatadogUpsellType,
} from '@hotjar/webapp/domains/revenue/upsell/constants';

interface PostTrialErrorBoundaryProps {
  children: React.ReactNode;
}

// todo: BUXP-569
// const FallbackErrorModal = () => (
//   <Modal size="fullscreen" hideCloseButton isOpen contentLabel="Error content Label">
//     An error occured
//   </Modal>
// )

/**
 * This component only acts as a wrapper of the PostTrialModalContainer.
 *
 * It is used to handle all sync error that may occur during the render lifecycle of any wrapped child component,
 * and any async error that happens when user interacts with the child components like "a button click".
 *
 * When an error occurs the onError function is called and this:
 * - sends the error to Sentry
 * - sends a Log to DataDog
 *
 * Sync Errors: See [Error Boundaries docs](https://reactjs.org/docs/error-boundaries.html)
 * Async Errors: We catch the errors by using the useErrorHandler(insights-common) hook and
 * that in turn triggers the onError function.
 */
export const PostTrialErrorBoundary = ({ children }: PostTrialErrorBoundaryProps): JSX.Element => {
  const onError = (error: unknown) => {
    if (error && error instanceof Error) {
      // Sentry Error Log
      logError(error);

      // DD Error Log
      upsellLogger?.error('Post Trial modal error', {
        upsellType: DatadogUpsellType.PostTrial,
        action: DatadogActionType.Failure,
        errorMessage: error.message,
      });
    }
  };

  return (
    <ErrorBoundary
      squadSentryTag="upsell"
      onError={onError}
      // todo: BUXP-569 - a fallback component with a try mechanism
      // fallbackComponent={FallbackErrorModal}
    >
      {children}
    </ErrorBoundary>
  );
};
